/**
 * 출·퇴근 기록 로컬 저장.
 *
 * ⚠ 서버 API 부재 상태의 임시 저장소다. 백엔드에 근태(attendance/punch) 엔드포인트가
 * 없어서(`/hr` 는 휴가·출장·초과근무만 제공) 출퇴근 시각을 브라우저에만 남긴다.
 * 서버 API 가 생기면 본 모듈의 `readToday`/`punchIn`/`punchOut` 만 API 호출로 교체하면
 * 위젯·모달 코드는 그대로 둘 수 있도록 경계를 여기 하나로 모았다.
 *
 * 기기·브라우저를 옮기면 기록이 따라가지 않는다 — 화면에도 그렇게 표시한다.
 */

const KEY = 'axi.attendance.local.v1';

export interface AttendanceRecord {
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM. 미기록이면 null. */
  checkIn: string | null;
  checkOut: string | null;
}

export function todayKey(now: Date = new Date()): string {
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function hhmm(now: Date): string {
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** 오늘 기록. 날짜가 바뀌었으면 빈 기록을 돌려준다(자동 리셋). */
export function readToday(now: Date = new Date()): AttendanceRecord {
  const date = todayKey(now);
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as AttendanceRecord;
      if (parsed?.date === date) return parsed;
    }
  } catch {
    /* 파싱 실패는 빈 기록과 같게 취급. */
  }
  return { date, checkIn: null, checkOut: null };
}

function write(rec: AttendanceRecord): AttendanceRecord {
  try {
    localStorage.setItem(KEY, JSON.stringify(rec));
  } catch {
    /* 저장 실패는 무시 — 화면 상태는 그대로 반영된다. */
  }
  return rec;
}

/** 출근 기록. 이미 있으면 덮어쓰지 않는다. */
export function punchIn(now: Date = new Date()): AttendanceRecord {
  const rec = readToday(now);
  if (rec.checkIn) return rec;
  return write({ ...rec, checkIn: hhmm(now) });
}

/** 퇴근 기록. 출근 전이면 출근도 함께 남긴다. */
export function punchOut(now: Date = new Date()): AttendanceRecord {
  const rec = readToday(now);
  return write({ ...rec, checkIn: rec.checkIn ?? hhmm(now), checkOut: hhmm(now) });
}

/**
 * 퇴근 기록만 지운다 — **퇴근 취소** (2026-09-06 · 판 C).
 *
 * ★ 대시보드 근태 위젯이 아직 이 로컬 기록을 본다. 서버에서 퇴근을 지웠는데 여기 남겨 두면
 *   같은 사람의 오늘이 **화면마다 달라진다**(위젯은 「퇴근 18:05」, 레일은 「근무 중」).
 * ★ 출근 시각은 **건드리지 않는다** — 취소한 것은 퇴근뿐이다.
 */
export function clearPunchOut(now: Date = new Date()): AttendanceRecord {
  const rec = readToday(now);
  if (!rec.checkOut) return rec;
  return write({ ...rec, checkOut: null });
}
